import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { listProfileRevisions } from "./api";
import type { ProfileRevision } from "./types";

export interface UseProfileRevisionsReturn {
  revisions: readonly ProfileRevision[];
  total: number;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export const profileRevisionsKey = (userId: string): readonly unknown[] =>
  ["profile-revisions", userId] as const;

/**
 * Revision history for one user.
 *
 * The monotonic request-id guard this used to carry is gone, not reimplemented:
 * `userId` is part of the query key, so reopening the panel on another user
 * while a read is in flight cannot apply the first user's response to the
 * second. That was the exact bug the counter existed to prevent, and a key
 * expresses it without a comparison anyone can write backwards.
 */
export function useProfileRevisions(userId: string): UseProfileRevisionsReturn {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: profileRevisionsKey(userId),
    queryFn: () => listProfileRevisions(userId),
  });

  const reload = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: profileRevisionsKey(userId) });
  }, [queryClient, userId]);

  return {
    revisions: query.data?.revisions ?? [],
    total: query.data?.total ?? 0,
    // `isPending` (nothing to show yet), not `isFetching` (revalidating), so a
    // reload keeps the painted list up instead of collapsing it to a spinner.
    isLoading: query.isPending,
    error:
      query.error === null
        ? null
        : query.error instanceof Error
          ? query.error.message
          : "Failed to load revision history",
    reload,
  };
}
